// The tabs on a person's page, in one place (round 27).
//
// ONE config, and everything reads from it: the tab strip, the counts beside
// each label, which folder an upload lands in, and where a reminder's `?tab=`
// deep link arrives. Zanetto Tools derives its tabs from the same kind of list
// for the same reason — a label, a count and an upload target that are written
// out separately drift apart, and the way you find out is a customer asking
// why the Inductions tab says 3 and lists 2.
import type { AttachmentCategory } from '../documentsApi';
import type { RecordCategory } from '../peopleApi';

export interface PersonSection {
  key: string;
  label: string;
  /** Set on the two tabs that hold records rather than loose files. */
  recordCategory?: RecordCategory;
  /** Which folder this tab's documents are filed under. */
  attachmentCategory?: AttachmentCategory;
  /** What to say when there is nothing in it yet. */
  empty?: string;
  addLabel?: string;
}

export const PERSON_SECTIONS: readonly PersonSection[] = [
  { key: 'overview', label: 'Overview' },
  // Away on its own tab (round 36), where a machine already keeps its
  // Schedule — same position, second after Overview. No count badge: a tally
  // of every entry including leave long since taken would read as a warning.
  { key: 'away', label: 'Away' },
  {
    key: 'competencies',
    label: 'Competencies',
    recordCategory: 'competency',
    attachmentCategory: 'competencies',
    empty: 'No licences, tickets or training recorded.',
    addLabel: 'Add competency',
  },
  {
    key: 'inductions',
    label: 'Inductions',
    recordCategory: 'induction',
    attachmentCategory: 'inductions',
    empty: 'No inductions recorded.',
    addLabel: 'Add induction',
  },
  {
    key: 'photos',
    label: 'Photos',
    attachmentCategory: 'photos',
    empty: 'No photos yet.',
  },
  {
    key: 'miscellaneous',
    label: 'Miscellaneous',
    attachmentCategory: 'miscellaneous',
    empty: 'Nothing filed here.',
  },
];

export const PERSON_TAB_KEYS = PERSON_SECTIONS.map((s) => s.key);

/**
 * The tabs on a MACHINE's page (round 34).
 *
 * Round 31 said a machine's page is not tabbed, and gave a good reason for its
 * documents: the folders are groups inside one panel, not tabs, because an
 * empty folder should draw nothing. That is still true and unchanged. What
 * round 34 changes is the page around them — Nathan walked it as a customer
 * and found meters, service history, prestarts and availability stacked down a
 * screen, with everything past the second panel below the fold. The job page
 * (round 33) and the person page (round 27) had already been through exactly
 * that, so this is the app settling on one shape rather than a new idea.
 *
 * Deliberately shorter than a person's, and deliberately not folder-shaped:
 * these group by QUESTION — what is it, when is it out, what has been done to
 * it, what paperwork does it have, what has been filled in against it.
 */
export const ASSET_SECTIONS: readonly PersonSection[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'service', label: 'Service' },
  { key: 'documents', label: 'Documents' },
  { key: 'forms', label: 'Pre-starts' },
];

export const ASSET_TAB_KEYS = ASSET_SECTIONS.map((s) => s.key);

/**
 * Every tab key either page can use.
 *
 * One list because `useTabParam` is called before the resource has loaded —
 * the kind is not known yet, so the hook cannot be given the right set. An
 * unrecognised value falls back to the first key, which is 'overview' on both.
 */
export const RESOURCE_TAB_KEYS = [...new Set([...PERSON_TAB_KEYS, ...ASSET_TAB_KEYS])];

/**
 * Which folder a document belongs in on screen.
 *
 * A person's document with no category reads as Miscellaneous — that is what
 * everything uploaded before folders existed looks like, and it needs somewhere
 * to appear rather than nowhere.
 */
export function folderOf(category: AttachmentCategory | null): AttachmentCategory {
  return category ?? 'miscellaneous';
}
